import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {deleteDraftItems} from 'common/src/modules/contentFeeds/store/contentFeedPage/thunks/deleteDraftItems.thunk';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';

const inputSchema = z.object({
  itemIds: z
    .array(z.number().int().positive())
    .min(1)
    .describe('One or more draft item IDs to delete from the current feed'),
});

export default function deleteDraftItemsTool(store: CLStore) {
  async function execute({
    input: {itemIds},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    const feedId = feed.id;
    const existingIds = new Set((state.contentFeedPage?.draftItems?.items ?? []).map((i) => i.id));
    const missing = itemIds.filter((id) => !existingIds.has(id));

    if (missing.length > 0) {
      return toolError(
        `Draft item ID(s) not found in the current list: ${missing.join(', ')}. Load draft items on the Draft Items step first.`,
      );
    }

    try {
      await store.dispatch(deleteDraftItems({feedId, itemIds})).unwrap();

      return toolResult(
        `Successfully deleted ${itemIds.length} draft item(s) (IDs: ${itemIds.join(', ')}). Draft list has been refreshed.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error deleting draft item(s): ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Deleting draft items',
    name: ContentFeedAiGeneratedSkillToolName.DeleteDraftItems,
    description:
      'Deletes one or more draft items from the current AI-generated content feed. The draft items list is refreshed from the server after deletion.',
    execute,
    inputSchema,
  });
}
